import { smartlook } from '@mafc/appshell';
import { smartlookErrorLabels } from '@mafc/common';
import EPaymentMethods from 'pages/Checkout/components/Payment/PamentMethods.enum';
import {
  IConfirmationOrderDetailsResponse,
  IPostOrderDetailsCitrusInfo,
  PaymentModeStatusList,
} from 'pages/OrderDetail/orderData.type';
import { EShipmentTypes } from 'services/shipments/types/shipments.types';
import { getGAId, getHashedEmailFromCookie } from 'utils/cookies.util';
import { getShipmentData } from 'utils/getSelectedAddress.util';
import Tracker from 'utils/Tracker';
import TrackerOrderConfirmation from 'utils/Tracker.orderConfirmation';
import { generateDeliveryTimeText } from './orderConfirmation.transform';
import { IOrderConfirmation } from '../OrderConfirmation.types';

export const getProductTotalQuantity = (productListAry: any) => {
  const totalQuantity = productListAry.length
    ? productListAry.reduce((productCount: number, product: { quantity: number }) => {
        productCount += product.quantity;

        return productCount;
      })
    : 0;
  return totalQuantity;
};

export const injectGooglePixelImageInDom = (orderNumber: string, orderMeta: any = {}) => {
  const { totalItems, totalPrice } = orderMeta;

  if (orderNumber && totalItems && totalPrice) {
    const hashedEmail = getHashedEmailFromCookie();
    const gaId = getGAId();
    const googlePixel: HTMLImageElement = document.createElement('img');
    googlePixel.setAttribute(
      'src',
      `https://pubads.g.doubleclick.net/activity;xsp=4785304;qty=${totalItems};cost=${totalPrice};ord=${hashedEmail +
        '-' +
        gaId +
        '-' +
        orderNumber}?`
    );
    googlePixel.setAttribute('height', '1');
    googlePixel.setAttribute('width', '1');
    googlePixel.setAttribute('border', '0');
    document.body.insertBefore(googlePixel, document.body.firstChild);
  }
  return null;
};

export const emitPurchaseEvent = (
  orderDetailResponse: IConfirmationOrderDetailsResponse,
  productsPurchasedData: any,
  loyalty: any,
  ga4: boolean = false
) => {
  const {
    paymentMode,
    paymentInfo,
    code: orderNumber,
    deliveryCost,
    totalPrice,
    appliedVouchers,
    deliveryAddress,
    shipments = [],
    cartId,
  } = orderDetailResponse;

  const containingShipmentTypes = [
    ...new Set(shipments.map(({ shipmentType }: { shipmentType: string }) => shipmentType)),
  ];
  const containsFood = containingShipmentTypes.includes(EShipmentTypes.STANDARD_FOOD);
  const [firstShipment = {}]: any[] = shipments;
  const eventData = {
    shipments_cnc: getShipmentData(shipments, EShipmentTypes.CNC),
    shipments_std_food: getShipmentData(shipments, EShipmentTypes.STANDARD_FOOD),
    shipments_std_nonfood: getShipmentData(shipments, EShipmentTypes.STANDARD_NONFOOD),
    shipments_mkt: getShipmentData(shipments, EShipmentTypes.MARKETPLACE),
    shipments_express: getShipmentData(shipments, EShipmentTypes.EXPRESS),
  };
  const purchaseEventOb = {
    payment: paymentMode === 'creditcard' ? paymentInfo && paymentInfo.cardType.code : paymentMode,
    cartId,
    id: orderNumber,
    shareFlag: paymentMode === EPaymentMethods.CASHBACK ? '1' : '0',
    revenue: totalPrice.value,
    shipping: deliveryCost && deliveryCost.value,
    coupon: appliedVouchers.map(({ code }: { code: string }) => code).join('|'),
    products: productsPurchasedData.gaProductList || [],
    deliveryDate: firstShipment.deliverySlot
      ? firstShipment.deliverySlot.deliveryDate
      : firstShipment.deliveryPromise,
    deliveryTime: firstShipment.deliverySlot
      ? generateDeliveryTimeText(firstShipment.deliverySlot)
      : firstShipment.deliveryPromise,
    orderType: containsFood
      ? containingShipmentTypes.length > 1
        ? 'food_non_food'
        : 'food'
      : 'non_food',
    deliveryArea:
      (deliveryAddress && deliveryAddress.deliveryArea && deliveryAddress.deliveryArea.name) || '',
    deliveryRegion:
      (deliveryAddress && deliveryAddress.region && deliveryAddress.region.name) || '',
    shipmentType: containingShipmentTypes || '',
    loyalityPoint: (loyalty && loyalty.totalPoints) || '',
    slotCode: (firstShipment && firstShipment.deliverySlot && firstShipment.deliverySlot.pos) || '',
    currency: (totalPrice && totalPrice.currencyIso) || '',
    shipmentCount: shipments.length,
    eventData,
    ga4,
    paymentMode,
  };
  Tracker.purchaseEvent({ ...purchaseEventOb });
};

export const extractDeliveryFee = (shipments: any, shipmentType: string) => {
  let shipmentData = { exists: false, fee: 0 };
  const shipment = !!shipments && shipments.find((item: any) => item.type === shipmentType);

  if (shipment) {
    shipmentData = {
      exists: true,
      fee: !!shipment.shipmentDeliveryFee ? shipment.shipmentDeliveryFee.value : 0,
    };
  }

  let shipmentDetail = 'false|delivery_fee=0';
  if (shipmentData.exists) {
    shipmentDetail = shipmentData.exists + '|delivery_fee=' + shipmentData.fee;
  }

  return shipmentDetail;
};

const getOrderCategory = (shipments: any) => {
  return shipments
    .reduce((acc: string[], shipment: any) => {
      const { shipmentType } = shipment;
      if (
        acc.indexOf(shipmentType) === -1 &&
        (shipmentType === EShipmentTypes.STANDARD_FOOD ||
          shipmentType === EShipmentTypes.EXPRESS ||
          shipmentType === EShipmentTypes.CNC)
      ) {
        acc.push('food');
      } else if (
        acc.indexOf(shipmentType) === -1 &&
        (shipmentType === EShipmentTypes.STANDARD_NONFOOD ||
          shipmentType === EShipmentTypes.MARKETPLACE)
      ) {
        acc.push('non_food');
      }

      return acc;
    }, [])
    .join('|');
};

export const getPurchaseEventData = (
  orderDetailResponse: IOrderConfirmation,
  productsPurchasedData: any
) => {
  const {
    paymentMode,
    orderMeta: { orderNumber },
    subTotal,
    appliedVouchers,
    shipmentsData = [],
    isNewUser,
    totalFees,
  } = orderDetailResponse;

  const eventData = {
    shipments_cnc: extractDeliveryFee(shipmentsData, EShipmentTypes.CNC),
    shipments_std_food: extractDeliveryFee(shipmentsData, EShipmentTypes.STANDARD_FOOD),
    shipments_std_nonfood: extractDeliveryFee(shipmentsData, EShipmentTypes.STANDARD_NONFOOD),
    shipments_mkt: extractDeliveryFee(shipmentsData, EShipmentTypes.MARKETPLACE),
    shipments_express: extractDeliveryFee(shipmentsData, EShipmentTypes.EXPRESS),
    shipments_qcomm: extractDeliveryFee(shipmentsData, EShipmentTypes.QCOMM),
    shipments_qelec: extractDeliveryFee(shipmentsData, EShipmentTypes.QELEC),
  };

  const shipmentType = getShipmentTypesForNewPurchase(shipmentsData);

  return {
    id: orderNumber,
    revenue: subTotal.value,
    coupon: appliedVouchers && appliedVouchers.map(({ code }: { code: string }) => code).join('|'),
    products: productsPurchasedData.gaProductListNew || [],
    currency: (subTotal && subTotal.currencyIso) || '',
    shipmentCount: shipmentsData.length,
    shipping: totalFees.value,
    shipmentType,
    shipmentCategory: getOrderCategoryForNewPurchase(shipmentsData),
    substitution: shipmentsData.some((shipment: any) =>
      shipment.products.some((product: any) => product.isSubstituted)
    )
      ? 'allowed'
      : 'not_allowed',
    eventData,
    paymentMode,
    isNewUser,
  };
};

const getOrderCategoryForNewPurchase = (shipments: any) => {
  return shipments
    .reduce((rootAcc: string[], shipment: any) => {
      return shipment.products.reduce((acc: string[], product: any) => {
        const productTypeInLower = product.productType.toLowerCase();

        if (acc.indexOf(productTypeInLower) === -1) {
          acc.push(productTypeInLower);
        }
        return acc;
      }, rootAcc);
    }, [])
    .sort()
    .join('|');
};

export const getShipmentTypesForNewPurchase = (shipments: any) => {
  return shipments
    .reduce((acc: string[], shipment: any) => {
      const shipmentTypeInLower = shipment.type.toLowerCase();

      if (
        shipment.type === EShipmentTypes.STANDARD_FOOD ||
        shipment.type === EShipmentTypes.STANDARD_NONFOOD
      ) {
        if (acc.indexOf('standard') === -1) {
          acc.push('standard');
        }
      } else if (acc.indexOf(shipmentTypeInLower) === -1) {
        acc.push(shipmentTypeInLower);
      }
      return acc;
    }, [])
    .sort()
    .join('|');
};

export const emitNewPurchaseEvent = (
  orderConfirmationData: IOrderConfirmation,
  productsPurchasedData: any
) => {
  const purchaseEventOb = getPurchaseEventData(orderConfirmationData, productsPurchasedData);

  TrackerOrderConfirmation.purchaseEvent({ ...purchaseEventOb });
};

export const transformCitrusData = (
  currencyRate: number,
  gaProductList: any,
  orderID: string
): IPostOrderDetailsCitrusInfo => {
  try {
    const productsData: any = gaProductList;
    const tranformProductData = productsData.map(
      (item: { id: string; quantity: number; price: number }) => {
        const { id = '', quantity = 0, price = 0 } = item;
        const priceInUSD = (currencyRate && Number((currencyRate * price).toFixed(4))) || price;
        return {
          gtin: id,
          quantity,
          regularUnitPrice: priceInUSD,
          totalOrderItemPriceAfterDiscounts: quantity * priceInUSD,
        };
      }
    );
    const gaId = getGAId();
    const clientId =
      (gaId &&
        gaId
          .split('.')
          .slice(-2)
          .join('.')) ||
      '';
    const isoDateFormat = new Date().toISOString();
    return {
      orders: [
        {
          id: orderID,
          sessionId: clientId,
          orderDate: (isoDateFormat && `${isoDateFormat.split('.')[0] + 'Z'}`) || '',
          orderItems: tranformProductData,
        },
      ],
    };
  } catch (e) {
    return {
      orders: [
        {
          id: orderID,
          sessionId: '',
          orderDate: '',
          orderItems: [],
        },
      ],
    };
  }
};

export const trackPaymentFailureLoyalty = (paymentModeStatusList: PaymentModeStatusList[]) => {
  const loyaltyMethod = paymentModeStatusList.find(
    (paymentMethod: PaymentModeStatusList) => paymentMethod.paymentMode === 'LOYALTY'
  );
  if (loyaltyMethod && loyaltyMethod.status === 'FAILED') {
    smartlook.track(smartlookErrorLabels.sharePointsPayFailure);
    return;
  }
  return;
};
